import {Alert, Dialog, openConfirmDialog, showNotification, Tabs} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useArchiveModelYearMutation,
  useListMakeQuery,
  useListModelQuery,
} from '@dms/api/new-vehicle';
import {NewVehiclePageHeader} from '@dms/features/new-vehicle';
import {vehiclesRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  refreshDmsDatagrid,
  usePermissions,
  useTenant,
} from '@dms/shared';

import {composePath, getApiDateString, useNavigate, type ApiError} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {handleCatalogueError, isStaleCatalogueVersion, joinParts, t} from './catalogueUi';
import {DuplicateModelYearDialog, type DuplicateModelYearTarget} from './DuplicateModelYearDialog';
import {FeaturesTab} from './EquipmentAuthoring';
import {newModelDraftKey, readNewModelDraft} from './newModelDraft';

const CATALOGUE_GRID_CODE = 'new-vehicle-model-year';
/** Row columns the catalogue grid exposes for the row actions. */
const MODEL_YEAR_COLUMNS = ['modelYear', 'model', 'version'] as const;

function CatalogueDraftBanner(props: {
  tenantId: string;
  userId: string;
  makes: {id: string; name: string}[];
  models: {id: string; name: string}[];
  onContinue: (step: number) => void;
}) {
  const key = newModelDraftKey(props.tenantId, props.userId);
  const [stored, setStored] = useState(() => readNewModelDraft(key));
  const [isDiscardOpen, setDiscardOpen] = useState(false);
  if (stored.status !== 'saved') {
    return null;
  }
  const draftName =
    joinParts([
      stored.draft.make.name || props.makes.find((make) => make.id === stored.draft.make.id)?.name,
      stored.draft.model.name ||
        props.models.find((model) => model.id === stored.draft.model.id)?.name,
    ]) || t('entity.newVehicle.authoring.unnamedModelDraft', 'Unnamed model');
  const discard = () => {
    try {
      sessionStorage.removeItem(key);
      setStored({status: 'empty'});
      setDiscardOpen(false);
    } catch {
      // The existing draft remains visible when browser storage is unavailable.
    }
  };
  return (
    <>
      <Alert
        layout="detailed"
        variant="info"
        message={t(
          'entity.newVehicle.authoring.unfinishedDraftBanner',
          'You have unfinished work saved in this browser: {name} (step {step} of 10).',
          {name: draftName, step: stored.step}
        )}
        actions={[
          {
            variant: 'primary',
            title: t('general.actions.continue', 'Continue'),
            onClick: () => props.onContinue(stored.step),
          },
          {
            variant: 'secondary',
            title: t('general.actions.discard', 'Discard'),
            onClick: () => setDiscardOpen(true),
          },
        ]}
      />
      <Dialog
        isOpen={isDiscardOpen}
        title={t('entity.newVehicle.authoring.discardDraftQuestion', 'Discard your changes?')}
        onClose={() => setDiscardOpen(false)}
        buttons={[
          {
            variant: 'secondary',
            title: t('general.actions.cancel', 'Cancel'),
            onClick: () => setDiscardOpen(false),
          },
          {
            variant: 'danger',
            title: t('general.actions.discard', 'Discard'),
            onClick: discard,
          },
        ]}
      >
        <Text>
          {t(
            'entity.newVehicle.authoring.discardDraftWarning',
            'This removes the unfinished work saved in this browser.'
          )}
        </Text>
      </Dialog>
    </>
  );
}

export function NewVehicleCatalogue() {
  const navigate = useNavigate();
  const [canWrite, canConfigure, canViewCost] = usePermissions({
    permissionKeys: [
      'newVehicleCatalogueWrite',
      'newVehicleConfigurationWrite',
      'newVehicleCatalogueViewCost',
    ],
  });
  const {tenant} = useTenant();
  const user = useGetCurrentUserInfoQuery();
  const makes = useListMakeQuery({}, {skip: !canWrite});
  const models = useListModelQuery({}, {skip: !canWrite});

  const [archiveModelYear] = useArchiveModelYearMutation();
  const [duplicateTarget, setDuplicateTarget] = useState<DuplicateModelYearTarget>();

  const openModelYear = (modelYearId: string) =>
    navigate(
      composePath(vehiclesRoutes.newVehicleCatalogueModelYear, {
        params: {modelYearId, tab: 'overview'},
      })
    );

  const modelYearAction: ActionCallback<typeof MODEL_YEAR_COLUMNS> = (params) => {
    const getColumnData = params.getColumnData;
    const rowId = Array.isArray(params.rowId) ? params.rowId[0] : params.rowId;
    const version = getColumnData<number>('version')?.value;
    const name = getColumnData<string>('modelYear')?.value ?? '';
    if (isNil(rowId)) {
      return;
    }

    return match(params.actionKey)
      .with('open', 'edit', () => openModelYear(rowId))
      .with('duplicate', () => {
        if (isNil(version)) {
          showNotification.error(t('general.labels.error', 'Something went wrong!'));
          return;
        }
        setDuplicateTarget({
          modelYearId: rowId,
          modelName: getColumnData<string>('model')?.value ?? '',
          version,
          validFrom: getApiDateString(new Date()),
          validTo: null,
        });
      })
      .with('archive', () => {
        if (isNil(version)) {
          showNotification.error(t('general.labels.error', 'Something went wrong!'));
          return;
        }
        openConfirmDialog({
          text: t(
            'entity.newVehicle.confirmations.archiveModelYear',
            'Archive model year {name}?',
            {
              name,
            }
          ),
          onConfirm: () =>
            void archiveModelYear({
              modelYearId: rowId,
              versionedWriteRequestBody: {version},
            })
              .unwrap()
              .then(() => params.refreshData())
              .catch((error: ApiError) => {
                handleCatalogueError(error);
                // A stale row version keeps failing until the grid refetches the current one.
                if (isStaleCatalogueVersion(error)) {
                  params.refreshData();
                }
              }),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  return (
    <VStack spacing={4}>
      <NewVehiclePageHeader
        breadcrumbs={[
          {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
          {
            label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
            href: vehiclesRoutes.newVehicleConfigurations,
          },
          {label: t('entity.newVehicle.catalogue.title', 'Catalogue')},
        ]}
        title={t('entity.newVehicle.catalogue.title', 'Catalogue')}
        actions={[
          ...(canWrite
            ? [
                {
                  type: 'button' as const,
                  title: t('entity.newVehicle.authoring.newModel', 'New model'),
                  leftIcon: 'content/add' as const,
                  variant: 'primary' as const,
                  onClick: () =>
                    navigate(
                      composePath(vehiclesRoutes.newVehicleCatalogueNewModel, {params: {step: '1'}})
                    ),
                },
              ]
            : []),
          ...(canConfigure
            ? [
                {
                  type: 'button' as const,
                  title: t('entity.newVehicle.actions.configure', 'Configure new vehicle'),
                  variant: 'secondary' as const,
                  onClick: () =>
                    navigate(
                      composePath(vehiclesRoutes.newVehicleConfigurator, {params: {step: '1'}})
                    ),
                },
              ]
            : []),
        ]}
      />
      <Show when={canWrite && tenant && user.data ? {tenant, user: user.data} : null}>
        {(session) => (
          <CatalogueDraftBanner
            tenantId={session.tenant.id}
            userId={session.user.id}
            makes={makes.data?.items ?? []}
            models={models.data?.items ?? []}
            onContinue={(step) =>
              navigate(
                composePath(vehiclesRoutes.newVehicleCatalogueNewModel, {
                  params: {step: String(step)},
                })
              )
            }
          />
        )}
      </Show>
      <Tabs
        hasSeparator
        tabs={[
          {
            id: 'catalogue',
            title: t('entity.newVehicle.catalogue.title', 'Catalogue'),
            content: (
              <DmsDataGrid
                gridCode={CATALOGUE_GRID_CODE}
                predefinedColumns={MODEL_YEAR_COLUMNS}
                actionCallback={modelYearAction}
              />
            ),
          },
          {
            id: 'equipment',
            title: t('entity.newVehicle.catalogue.featureLibrary', 'Equipment library'),
            content: <FeaturesTab canWrite={canWrite} canViewCost={canViewCost} />,
          },
        ]}
      />
      <DuplicateModelYearDialog
        target={duplicateTarget}
        onClose={() => setDuplicateTarget(undefined)}
        onDuplicated={openModelYear}
        onStaleVersion={() => refreshDmsDatagrid(CATALOGUE_GRID_CODE)}
      />
    </VStack>
  );
}
